import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { useToast } from '@platform/ui';

import {
  AppointmentDetailError,
  AppointmentDetailLoading,
  AppointmentDetailView,
} from '../../features/appointments/appointment-detail';
import { formatLocalDate } from '../../features/scheduling/availability-preview';
import { useOperationsClient } from '../../lib/api/operations-client-context';
import { newIdempotencyKey } from '../../lib/api/payments';
import { useReviewClient } from '../../lib/api/review-client-context';
import { useSchedulingClient } from '../../lib/api/scheduling-client-context';
import { describeApiError } from '../../lib/session/describe-error';
import { useSessionFlags } from '../../lib/session/flags';
import { usePermissions } from '../../lib/session/permissions';

export const Route = createFileRoute('/appointments/$appointmentId')({
  component: AppointmentDetailPage,
});

/**
 * The cancellation policy now comes from `GET /admin/appointments/:id` itself
 * (`AdminAppointment.policy`, M7.8), so the hard-coded fee is gone. These are the last
 * resort for a detail that somehow arrived without one.
 */
const FALLBACK_LATE_CANCEL_FEE_CENTS = 5000;
const FALLBACK_NO_SHOW_FEE_CENTS = 7500;

/**
 * `/appointments/$appointmentId` — one visit (MASTER_PLAN M11.2, Docs/API.md §2.5).
 *
 * Every action is a mutation that invalidates the appointment and its timeline, so the
 * timeline is the proof that the action happened — which is exactly how staff will use it.
 */
function AppointmentDetailPage() {
  const { appointmentId } = Route.useParams();
  const client = useOperationsClient();
  const scheduling = useSchedulingClient();
  /* M6.9. The re-run is a real endpoint - `POST /admin/appointments/:id/eligibility/rerun`
     (insurance.check) - and goes through the review seam rather than the operations mock. */
  const review = useReviewClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { can } = usePermissions();
  const { impersonating, impersonatorEmail } = useSessionFlags();

  const [actionError, setActionError] = useState<string | undefined>(undefined);
  const [slotDate, setSlotDate] = useState<string>(() =>
    formatLocalDate(new Date().getTime(), 'America/Los_Angeles'),
  );

  const detail = useQuery({
    queryKey: ['appointment', appointmentId],
    queryFn: () => client.getAppointment(appointmentId),
  });
  /**
   * The timeline is cursor paginated (API.md §2.5), so "Load older entries" carries the
   * cursor forward and appends rather than re-reading a bigger page. A visit that has run
   * for months has more events than one read returns, and a list that silently stopped at
   * fifty would read as a complete history (`ADMIN_OPERATIONS_UI.md` §6 item 10).
   */
  const timeline = useInfiniteQuery({
    queryKey: ['appointment-timeline', appointmentId],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      client.listTimeline(appointmentId, { limit: 50, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  /* A different endpoint and a different permission (`notifications.read`): a staff
     member who may see a visit is not thereby entitled to the messages sent to the
     person it belongs to (API.md §2.9). */
  const notifications = useQuery({
    queryKey: ['appointment-notifications', appointmentId],
    queryFn: () => client.listAppointmentNotifications(appointmentId),
  });

  const providers = useQuery({
    queryKey: ['providers'],
    queryFn: () => scheduling.listProviders(),
  });

  /*
   * The paperwork is its own query and it is **not** loaded with the appointment: reading
   * it decrypts the sensitive answers and writes `event.intake.viewed`, so opening a
   * visit to move it must not also record that somebody read the patient's answers
   * (API.md §2.8, rules §33.2). The Intake tab is the read.
   */
  const intake = useQuery({
    queryKey: ['appointment-intake', appointmentId],
    queryFn: () => client.getAppointmentIntake(appointmentId),
    /* No retry: a second audited PHI read for a failure the person can see is worse
       than the failure. */
    retry: false,
  });

  const slots = useQuery({
    queryKey: ['reschedule-slots', appointmentId, slotDate],
    queryFn: () => client.listRescheduleSlots(appointmentId, slotDate),
    enabled: detail.data !== undefined,
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['appointment', appointmentId] });
    await queryClient.invalidateQueries({ queryKey: ['appointment-timeline', appointmentId] });
    await queryClient.invalidateQueries({ queryKey: ['appointment-notifications', appointmentId] });
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    await queryClient.invalidateQueries({ queryKey: ['appointments'] });
  };

  /**
   * The export, and the download it produces (M8.8, FR-INTAKE-06).
   *
   * A fresh `Idempotency-Key` per attempt the person made, for the same reason the
   * payment panel makes one: a double-clicked button must be one export, one file and
   * one `intake.exported` row rather than two (API.md §1.8).
   *
   * The PDF is opened from the signed URL rather than fetched into the page: the link is
   * a five-minute bearer credential for PHI, and a blob the tab is holding is one more
   * copy of it in memory. `rel="noopener"` because the URL is a storage origin.
   */
  const exportIntake = useMutation({
    mutationFn: () => client.exportAppointmentIntake(appointmentId, newIdempotencyKey()),
    onSuccess: (result) => {
      setActionError(undefined);
      /* `download=` is on the signed URL server-side, so the browser saves the file
         rather than rendering PHI inline in a tab that stays open (API.md §2.8). */
      globalThis.open(result.url, '_blank', 'noopener,noreferrer');
      toast({
        tone: 'success',
        message: `Version ${String(result.formVersion)} exported. The link works for five minutes.`,
      });
    },
    onError: (error: Error) => {
      const message = describeApiError(error);
      setActionError(message);
      toast({ tone: 'error', message });
    },
  });

  const reviewIntake = useMutation({
    mutationFn: (submissionId: string) => client.reviewIntakeSubmission(submissionId),
    onSuccess: async () => {
      setActionError(undefined);
      toast({ tone: 'success', message: 'Marked as reviewed.' });
      await queryClient.invalidateQueries({ queryKey: ['appointment-intake', appointmentId] });
    },
    onError: (error: Error) => {
      const message = describeApiError(error);
      setActionError(message);
      toast({ tone: 'error', message });
    },
  });

  /**
   * `202 { queued }` — and `queued: false` is a **result**, not a failure (API.md §2.5).
   *
   * The write-back job has a sixty-second singleton window, so a second press inside it
   * changes nothing. Saying "re-queued" both times would tell somebody watching a stuck
   * visit that two attempts had been made when one had.
   */
  const retryEhr = useMutation({
    mutationFn: () => client.retryEhrSync(appointmentId),
    onSuccess: async (result) => {
      setActionError(undefined);
      toast({
        tone: result.queued ? 'success' : 'info',
        message: result.queued
          ? 'EHR write re-queued.'
          : 'A retry is already queued for this visit. Nothing more was sent.',
      });
      await refresh();
    },
    onError: (error: Error) => {
      const message = describeApiError(error);
      setActionError(message);
      toast({ tone: 'error', message });
    },
  });

  const resendConfirmation = useMutation({
    mutationFn: () => client.resendConfirmation(appointmentId),
    onSuccess: async () => {
      setActionError(undefined);
      toast({ tone: 'success', message: 'Confirmation queued again.' });
      await refresh();
    },
    onError: (error: Error) => {
      const message = describeApiError(error);
      setActionError(message);
      toast({ tone: 'error', message });
    },
  });

  /** One mutation runner: every action reports the same way (rules §17.7). */
  const run = useMutation({
    mutationFn: async ({ action }: { action: () => Promise<unknown>; message: string }) => {
      await action();
    },
    onSuccess: async (_result, variables) => {
      setActionError(undefined);
      toast({ tone: 'success', message: variables.message });
      await refresh();
    },
    onError: (error: Error) => {
      const message = describeApiError(error);
      setActionError(message);
      toast({ tone: 'error', message });
    },
  });

  const act = (message: string, action: () => Promise<unknown>): void => {
    run.mutate({ action, message });
  };

  if (detail.isPending) return <AppointmentDetailLoading />;
  if (detail.isError) {
    return <AppointmentDetailError message={describeApiError(detail.error)} />;
  }

  return (
    <AppointmentDetailView
      detail={detail.data}
      timeline={timeline.data?.pages.flatMap((page) => page.data) ?? []}
      timelineLoading={timeline.isPending}
      timelineError={timeline.isError ? describeApiError(timeline.error) : undefined}
      onLoadMoreTimeline={
        timeline.hasNextPage
          ? () => {
              void timeline.fetchNextPage();
            }
          : undefined
      }
      timelineLoadingMore={timeline.isFetchingNextPage}
      notifications={notifications.data ?? []}
      notificationsLoading={notifications.isPending}
      notificationsError={notifications.isError ? describeApiError(notifications.error) : undefined}
      resendingConfirmation={resendConfirmation.isPending}
      intakeSummary={intake.data}
      intakeLoading={intake.isPending}
      intakeError={intake.isError ? describeApiError(intake.error) : undefined}
      intakeExporting={exportIntake.isPending}
      intakeReviewing={reviewIntake.isPending}
      canExportIntake={can('intake.read')}
      canReviewIntake={can('intake.review')}
      intakeExportBlockedReason={
        impersonating
          ? `Blocked while ${impersonatorEmail ?? 'a platform administrator'} is viewing as you. An export outlives the support session, so it is the one action here that is refused outright.`
          : undefined
      }
      providers={(providers.data ?? []).map((provider) => ({
        id: provider.id,
        name: provider.displayName,
      }))}
      slots={slots.data?.data ?? []}
      slotsEmptyReason={slots.data?.emptyReason ?? null}
      slotsLoading={slots.isFetching}
      slotDate={slotDate}
      lateCancelFeeCents={
        detail.data.appointment.policy.lateCancelFeeCents || FALLBACK_LATE_CANCEL_FEE_CENTS
      }
      noShowFeeCents={detail.data.appointment.policy.noShowFeeCents || FALLBACK_NO_SHOW_FEE_CENTS}
      busy={run.isPending}
      actionError={actionError}
      actions={{
        onLoadSlots: (date) => {
          setSlotDate(date);
        },
        onReschedule: (input) => {
          act('Appointment moved.', () =>
            client.rescheduleAppointment(appointmentId, {
              startAt: input.startAt,
              providerId: input.providerId,
              notifyPatient: input.notifyPatient,
            }),
          );
        },
        onReassign: (providerId) => {
          /* Its own endpoint, not a reschedule to the same time: moving a visit to
             another clinician is a different decision and the API records it as one. */
          act('Provider reassigned.', () => client.reassignProvider(appointmentId, providerId));
        },
        onCancel: (input) => {
          act('Appointment cancelled.', () => client.cancelAppointment(appointmentId, input));
        },
        onComplete: () => {
          act('Marked completed.', () => client.completeAppointment(appointmentId));
        },
        onMarkNoShow: () => {
          /* The fee is not applied from here: "Apply fee" on the Payment tab is the
             deliberate act, next to the amount it charges (FR-SCHED-10). */
          act('Marked as a no-show.', () => client.markNoShow(appointmentId, false));
        },
        onAddNote: (body) => {
          act('Note added.', () => client.addNote(appointmentId, body));
        },
        onResendConfirmation: () => {
          resendConfirmation.mutate();
        },
        onSendIntakeReminder: () => {
          act('Intake reminder queued.', () => client.sendIntakeReminder(appointmentId));
        },
        onExportIntake: () => {
          exportIntake.mutate();
        },
        onMarkIntakeReviewed: () => {
          const submissionId = intake.data?.submissionId;
          if (submissionId === undefined) return;
          reviewIntake.mutate(submissionId);
        },
        onRetryEhr: () => {
          retryEhr.mutate();
        },
        onRerunEligibility: () => {
          /* `202`: the check is queued, not answered. Saying "re-checked" would claim an
             answer the payer has not given yet. */
          act('Coverage check queued. It updates here when the payer answers.', () =>
            review.rerunForAppointment(appointmentId),
          );
        },
      }}
      renderLink={(to, children, className): ReactNode => (
        <Link to={to} className={className}>
          {children}
        </Link>
      )}
    />
  );
}
